import { z } from 'zod';
import { Dateish } from './common';

// ── Status do atendente (spec 015) ──────────────────────────────────────────
/**
 * `available` = pode receber conversa; `paused` = pausa COM motivo (conta no
 * relatório); `away` = fora do expediente (NÃO é pausa). Sem intervalo aberto
 * no log a pessoa está `away` — é o estado de quem nunca entrou ou de quem
 * teve a pausa encerrada pelo admin depois de fechar o navegador.
 */
export const AttendantStatusSchema = z.enum(['available', 'paused', 'away']);
export type AttendantStatus = z.infer<typeof AttendantStatusSchema>;

export const ATTENDANT_STATUS_LABELS: Record<AttendantStatus, string> = {
  available: 'Disponível',
  paused: 'Em pausa',
  away: 'Ausente',
};

export const PAUSE_REASON_NAME_MAX = 120;

/**
 * Motivos iniciais de todo tenant — a migration semeia os existentes e um
 * trigger em erp.tenants semeia os novos com ESTA lista (a migration repete os
 * textos; este array é a referência para testes e para o front).
 */
export const DEFAULT_PAUSE_REASONS = [
  'Almoço',
  'Intervalo',
  'Reunião',
  'Treinamento',
  'Problema técnico',
] as const;

/** status corrente de uma pessoa da equipe (GET /presence, /presence/me) */
export const PresenceSchema = z.object({
  userId: z.number().int(),
  status: AttendantStatusSchema,
  /** só em `paused` */
  reasonId: z.number().int().nullable(),
  reasonName: z.string().nullable(),
  /** início do intervalo corrente; null = sem intervalo aberto (away) */
  since: Dateish.nullable(),
  /**
   * só no /presence/me, em `away`: o fim do último intervalo, quando ele
   * fechou por inatividade e nada abriu depois (spec 072) — o "desde" do aviso
   * de volta. null em todo outro caso.
   */
  idleSince: Dateish.nullable(),
});
export type Presence = z.infer<typeof PresenceSchema>;

// ── Ausência por inatividade (spec 072) ─────────────────────────────────────
/** padrão da organização sem configuração salva */
export const PRESENCE_IDLE_DEFAULT_MINUTES = 60;
export const PRESENCE_IDLE_MIN_MINUTES = 15;
export const PRESENCE_IDLE_MAX_MINUTES = 480;
/** o motivo do Ausente de quem caiu por inatividade, onde a tela o mostra */
export const PRESENCE_IDLE_REASON_LABEL = 'Sem atividade';
/** a aba avisa a atividade da pessoa no máximo neste intervalo */
export const PRESENCE_ACTIVITY_THROTTLE_MS = 60_000;

/** 0 = desligado; senão, de 15 a 480 min */
export function isValidIdleMinutes(minutes: number): boolean {
  return (
    Number.isInteger(minutes) &&
    (minutes === 0 || (minutes >= PRESENCE_IDLE_MIN_MINUTES && minutes <= PRESENCE_IDLE_MAX_MINUTES))
  );
}

/** GET/PUT /presence/settings */
export const PresenceSettingsSchema = z.object({
  /** sem atividade por este tempo, Disponível vira Ausente; 0 = desligado */
  idleMinutes: z.number().int(),
});
export type PresenceSettings = z.infer<typeof PresenceSettingsSchema>;

/** a faixa vira `422 idle_minutes_invalid` no use-case, código que a tela entende */
export const PresenceSettingsPutBodySchema = z
  .object({
    idleMinutes: z.number().int(),
  })
  .strict();
export type PresenceSettingsPutBody = z.infer<typeof PresenceSettingsPutBodySchema>;

/**
 * POST /presence/me. `reasonId` é nullish no schema de propósito: a ausência
 * em `paused` precisa virar `422 pause_reason_required` (código que a tela
 * entende), não o `invalid_body` genérico do parse.
 */
export const PresenceSetBodySchema = z
  .object({
    status: AttendantStatusSchema,
    reasonId: z.number().int().positive().nullish(),
  })
  .strict();
export type PresenceSetBody = z.infer<typeof PresenceSetBodySchema>;

/**
 * POST /presence/:userId/end (admin) — o horário REAL em que a pessoa parou:
 * quem esqueceu a pausa aberta e foi embora não pode ficar com a noite
 * inteira de pausa no relatório.
 */
export const PresenceEndBodySchema = z
  .object({
    endedAt: z.coerce.date(),
  })
  .strict();
export type PresenceEndBody = z.infer<typeof PresenceEndBodySchema>;

// ── Motivos de pausa ────────────────────────────────────────────────────────
export const PauseReasonSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** desativado some da escolha, mas continua nos intervalos antigos */
  active: z.boolean(),
  position: z.number().int(),
});
export type PauseReason = z.infer<typeof PauseReasonSchema>;

export const PauseReasonCreateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(PAUSE_REASON_NAME_MAX),
    active: z.boolean().default(true),
  })
  .strict();
export type PauseReasonCreateBody = z.infer<typeof PauseReasonCreateBodySchema>;

export const PauseReasonUpdateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(PAUSE_REASON_NAME_MAX),
    active: z.boolean(),
  })
  .partial()
  .strict();
export type PauseReasonUpdateBody = z.infer<typeof PauseReasonUpdateBodySchema>;

/** PUT /presence/pause-reasons/order — a lista COMPLETA de ids na ordem nova */
export const PauseReasonReorderBodySchema = z.object({
  ids: z.array(z.number().int().positive()).min(1),
});
export type PauseReasonReorderBody = z.infer<typeof PauseReasonReorderBodySchema>;
